import { Flex, Form, Modal } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { Text } from 'components/mvp-typography';
import { ConcussionRXLogo } from 'features/sign-in/v2/concussionRx-logo';
import { styles } from 'features/sign-in/v2/styles';
import { resolveError } from 'lib/utils/formatters/error/resolve-error';
import { useErrorList } from 'lib/utils/useErrorList';
import { CSSProperties, ReactNode, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormQuestion } from '../types/form-questions';
import { FormArgs, FormItem, RenderQuestion } from './user-add-question';

export interface UserAddModalWrapperProps {
  onAddUser: (data: FormArgs) => Promise<void | any> | void | any;
  /**
   * The type of user to create. Must be in the singular form
   */
  type: string;
  containerStyle?: CSSProperties;
  buttonStyle?: CSSProperties;
  questions: FormQuestion[];
  disabled?: boolean;
}

export function UserAddModalWrapper(props: UserAddModalWrapperProps) {
  const typeToCreate = props.type.toLowerCase();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={props.containerStyle}>
      <BaseButton
        style={props.buttonStyle}
        disabled={props.disabled}
        onClick={() => {
          setIsOpen(true);
        }}
      >
        Add {typeToCreate}
      </BaseButton>
      <UserEditableDetailsModal
        {...props}
        onSubmit={props.onAddUser}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        submitText={`Create ${typeToCreate}`}
        actionProcessingDescription={`Sending invitation. Please do not close or reload this window or tab.`}
        questions={props.questions}
      />
    </div>
  );
}

export interface UserEditableDetailsModalProps {
  onSubmit: (data: FormArgs) => Promise<void | any> | void | any;
  isOpen: boolean;
  setIsOpen: (s: boolean) => void;
  submitText: ReactNode;
  actionProcessingDescription: ReactNode;
  initialValues?: Record<string, string | undefined | null>;
  questions: FormQuestion[];
}

export function UserEditableDetailsModal(props: UserEditableDetailsModalProps) {
  const errorList = useErrorList();
  const [isActionProcessing, setIsActionProcessing] = useState(false);
  const [form] = useForm();
  async function onAddUserSubmitEvent(event: FormArgs) {
    setIsActionProcessing(true);
    try {
      errorList.reset();
      await props.onSubmit(event);
      props.setIsOpen(false);
    } catch (e) {
      const error = resolveError(e);
      errorList.addError(error);
    } finally {
      setIsActionProcessing(false);
    }
  }

  return (
    <Modal
      open={props.isOpen}
      onCancel={() => props.setIsOpen(false)}
      closable={!isActionProcessing}
      footer={() => <></>}
    >
      <Flex
        style={{
          ...styles.titleContainer
        }}
        justify="center"
      >
        <div
          style={{
            maxWidth: 300
          }}
        >
          <ConcussionRXLogo />
        </div>
      </Flex>
      <Form
        form={form}
        onFinish={onAddUserSubmitEvent}
        disabled={isActionProcessing}
        layout="vertical"
        initialValues={props.initialValues}
      >
        {props.questions.map(question => (
          <ErrorBoundary
            key={question.name}
            fallbackRender={props => {
              const error = resolveError(props.error);
              return <Text>{error.message}</Text>;
            }}
          >
            <RenderQuestion question={question} />
          </ErrorBoundary>
        ))}

        <FormItem>
          <div>
            <BaseButton
              htmlType="submit"
              loading={isActionProcessing}
              style={{
                padding: '1rem 2rem',
                height: 'auto'
              }}
            >
              {props.submitText}
            </BaseButton>
            {isActionProcessing && (
              <Text>{props.actionProcessingDescription}</Text>
            )}
          </div>
        </FormItem>
      </Form>

      <ErrorListDisplay errorList={errorList} />
    </Modal>
  );
}
